'use client';

import React, { useState } from 'react';
import { DailyRecord } from './dailyRecordsData';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { MoreVertical, Edit2, CheckCircle, Trash2, ChevronDown, ChevronUp, Egg, Thermometer, Droplets, ClipboardList } from 'lucide-react';

interface Props {
  records: DailyRecord[];
  onEdit: (record: DailyRecord) => void;
  onApprove: (id: string) => void;
  onDelete: (id: string) => void;
}

function statusVariant(status: string): 'success' | 'warning' | 'info' | 'default' {
  if (status === 'approved') return 'success';
  if (status === 'submitted') return 'warning';
  return 'default';
}

export default function DailyRecordsTable({ records, onEdit, onApprove, onDelete }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);

  if (records.length === 0) {
    return (
      <EmptyState
        title="No daily records found"
        description="Click 'New Record' to enter today's flock data."
        icon={ClipboardList}
      />
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ backgroundColor: 'var(--muted)' }}>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Flock</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">House</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Opening</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Mortality</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Culled</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Closing</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Feed (kg)</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Eggs</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Temp °C</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {records.map((r) => (
              <React.Fragment key={r.id}>
                <tr
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                >
                  <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {expandedId === r.id
                        ? <ChevronUp size={13} className="text-muted-foreground" />
                        : <ChevronDown size={13} className="text-muted-foreground" />}
                      {r.date}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground text-xs">{r.flockNumber}</div>
                    <div className="text-muted-foreground text-xs truncate max-w-[140px]">{r.flockName}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{r.house}</td>
                  <td className="px-4 py-3 text-right font-tabular text-foreground">{r.openingCount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-tabular">
                    <span style={{ color: r.mortality > 0 ? 'var(--danger)' : 'var(--muted-foreground)' }}>
                      {r.mortality}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-tabular">
                    <span style={{ color: r.culled > 0 ? 'var(--warning)' : 'var(--muted-foreground)' }}>
                      {r.culled}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-tabular font-semibold" style={{ color: 'var(--primary)' }}>
                    {r.closingCount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-tabular text-foreground">{r.feedConsumed}</td>
                  <td className="px-4 py-3 text-right font-tabular text-foreground">
                    {r.eggsProduced > 0 ? r.eggsProduced.toLocaleString() : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-tabular text-foreground">{r.avgTemperature}°</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} variant={statusVariant(r.status)} />
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <button
                        onClick={() => setMenuId(menuId === r.id ? null : r.id)}
                        className="p-1.5 rounded hover:bg-slate-100 transition-colors"
                      >
                        <MoreVertical size={14} className="text-muted-foreground" />
                      </button>
                      {menuId === r.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuId(null)} />
                          <div className="absolute right-0 top-8 z-20 w-44 card shadow-lg border py-1 text-sm" style={{ borderColor: 'var(--border)' }}>
                            <button
                              onClick={() => { onEdit(r); setMenuId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                            >
                              <Edit2 size={13} /> Edit Record
                            </button>
                            {r.status === 'submitted' && (
                              <button
                                onClick={() => { onApprove(r.id); setMenuId(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                                style={{ color: 'var(--success)' }}
                              >
                                <CheckCircle size={13} /> Approve
                              </button>
                            )}
                            <button
                              onClick={() => { onDelete(r.id); setMenuId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                              style={{ color: 'var(--danger)' }}
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Expanded row */}
                {expandedId === r.id && (
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <td colSpan={12} className="px-6 py-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <p className="text-muted-foreground font-medium mb-1">Feed</p>
                          <p>Issued: <strong>{r.feedIssued} kg</strong></p>
                          <p>Consumed: <strong>{r.feedConsumed} kg</strong></p>
                          {r.closingCount > 0 && (
                            <p className="text-muted-foreground">Per bird: {(r.feedConsumed / r.closingCount * 1000).toFixed(0)}g</p>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground font-medium mb-1">Water</p>
                          <p>Issued: <strong>{r.waterIssued} L</strong></p>
                          <p>Consumed: <strong>{r.waterConsumed} L</strong></p>
                          {r.closingCount > 0 && (
                            <p className="text-muted-foreground">Per bird: {(r.waterConsumed / r.closingCount).toFixed(2)}L</p>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground font-medium mb-1">Environment</p>
                          <p className="flex items-center gap-1"><Thermometer size={11} /> Temp: <strong>{r.avgTemperature}°C</strong></p>
                          <p className="flex items-center gap-1"><Droplets size={11} /> Humidity: <strong>{r.humidity}%</strong></p>
                          {r.eggsProduced > 0 && (
                            <p className="flex items-center gap-1 mt-1"><Egg size={11} /> Eggs: <strong>{r.eggsProduced.toLocaleString()}</strong></p>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground font-medium mb-1">Observations</p>
                          <p className="text-foreground leading-relaxed">{r.observations || '—'}</p>
                          <p className="text-muted-foreground mt-1">By: {r.recordedBy}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y" style={{ borderColor: 'var(--border)' }}>
        {records.map((r) => (
          <div key={r.id} className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-semibold text-sm text-foreground">{r.flockNumber}</div>
                <div className="text-xs text-muted-foreground">{r.date} · {r.house}</div>
              </div>
              <StatusBadge status={r.status} variant={statusVariant(r.status)} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mb-2">
              <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--muted)' }}>
                <div className="text-muted-foreground">Opening</div>
                <div className="font-semibold font-tabular">{r.openingCount.toLocaleString()}</div>
              </div>
              <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--muted)' }}>
                <div className="text-muted-foreground">Dead/Culled</div>
                <div className="font-semibold font-tabular" style={{ color: 'var(--danger)' }}>{r.mortality + r.culled}</div>
              </div>
              <div className="text-center p-2 rounded" style={{ backgroundColor: '#f0fdf4' }}>
                <div className="text-muted-foreground">Closing</div>
                <div className="font-semibold font-tabular" style={{ color: 'var(--primary)' }}>{r.closingCount.toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
              <span>Feed: <strong className="text-foreground">{r.feedConsumed}kg</strong></span>
              <span>Temp: <strong className="text-foreground">{r.avgTemperature}°C</strong></span>
              {r.eggsProduced > 0 && <span>Eggs: <strong className="text-foreground">{r.eggsProduced.toLocaleString()}</strong></span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onEdit(r)} className="btn-secondary text-xs py-1 px-2">Edit</button>
              {r.status === 'submitted' && (
                <button onClick={() => onApprove(r.id)} className="text-xs py-1 px-2 rounded font-medium" style={{ backgroundColor: 'var(--success-bg)', color: 'var(--success)' }}>Approve</button>
              )}
              <button onClick={() => onDelete(r.id)} className="text-xs py-1 px-2 rounded font-medium" style={{ backgroundColor: 'var(--danger-bg)', color: 'var(--danger)' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
